import { useState, type SyntheticEvent } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router-dom'

import { useAuth } from '@/lib/auth/auth-context'
import { canOpenPath } from '@/lib/auth/roles'
import { clearDeliberateSignOut } from '@/lib/auth/sign-out-intent'
import {
  completeNewPassword,
  confirmEmailOtp,
  confirmTotpCode,
  requestEmailOtp,
  signInWithPassword,
  type SignInOutcome,
} from '@/lib/auth/sign-in'
import { markSessionActive } from '@/lib/auth/use-idle-timeout'

const SESSION_EXPIRED = 'Your session has ended. Sign in again to continue.'

const OTP_UNSUPPORTED = 'This account needs a different sign-in method.'

type SignInMethod = 'password' | 'otp'

const fieldClass =
  'bg-surface border-border-strong text-text placeholder:text-text-3 focus:border-green-400 w-full rounded-lg border px-3 py-2.5 text-[13px] outline-none transition-colors disabled:opacity-60'

const labelClass = 'text-text mb-1.5 block text-[13px] font-medium'

const tabClass =
  'flex-1 cursor-pointer border-b-2 px-1 py-3 text-[13px] font-medium whitespace-nowrap transition-colors'

const tabActiveClass = 'border-green-600 text-green-800'

const tabIdleClass = 'border-transparent text-text-3 hover:text-text'

const submitClass =
  'canopy-btn canopy-btn-primary w-full justify-center rounded-lg py-2.5 text-[13px]'

const secondaryClass = 'canopy-btn mt-2 w-full justify-center rounded-lg py-2.5 text-[13px]'

export function Login() {
  const [params] = useSearchParams()
  const location = useLocation()
  const { session, refresh } = useAuth()

  const [method, setMethod] = useState<SignInMethod>('password')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [needsNewPassword, setNeedsNewPassword] = useState(false)

  const [otpEmail, setOtpEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpStep, setOtpStep] = useState<'request' | 'verify'>('request')

  const [needsTotp, setNeedsTotp] = useState(false)
  const [totpCode, setTotpCode] = useState('')

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const expired = params.get('error') === 'session-expired'
  const notice = error ?? (expired ? SESSION_EXPIRED : null)

  const onEntryScreen =
    !needsTotp && (method === 'password' ? !needsNewPassword : otpStep === 'request')

  const intro = needsTotp
    ? 'Enter the 6-digit code from your authenticator app.'
    : needsNewPassword
      ? 'Your account was created with a temporary password. Choose a permanent one to continue.'
      : method === 'otp'
        ? otpStep === 'verify'
          ? `Enter the code we emailed to ${otpEmail}.`
          : "Enter your email and we'll send you a one-time code."
        : 'Enter your email and password to continue.'

  async function handleOutcome(outcome: SignInOutcome, flow: SignInMethod): Promise<void> {
    switch (outcome.status) {
      case 'signed-in':
        markSessionActive()
        clearDeliberateSignOut()
        await refresh()
        return
      case 'new-password-required':
        if (flow === 'otp') {
          setError(OTP_UNSUPPORTED)
          return
        }
        setNeedsNewPassword(true)
        setError(null)
        return
      case 'reset-password-required':
        setError(
          flow === 'otp'
            ? OTP_UNSUPPORTED
            : 'Your password needs resetting before you can sign in.',
        )
        return
      case 'otp-required':
        setOtpStep('verify')
        setError(null)
        return
      case 'totp-required':
        setNeedsTotp(true)
        setError(null)
        return
      case 'unsupported-challenge':
        setError(
          flow === 'otp'
            ? OTP_UNSUPPORTED
            : `This account needs a sign-in step this application does not support yet (${outcome.step}). Contact your administrator.`,
        )
        return
      case 'failed':
        setError(outcome.message)
        return
    }
  }

  function resetForms(): void {
    setEmail('')
    setPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setNeedsNewPassword(false)
    setOtpEmail('')
    setOtpCode('')
    setOtpStep('request')
    setNeedsTotp(false)
    setTotpCode('')
    setError(null)
    setBusy(false)
  }

  function switchMethod(next: SignInMethod): void {
    if (next === method) return

    setMethod(next)
    resetForms()
  }

  function startOver(): void {
    resetForms()
  }

  async function submitCredentials(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      await handleOutcome(await signInWithPassword(email, password), 'password')
    } finally {
      setBusy(false)
    }
  }

  async function submitNewPassword(event: SyntheticEvent): Promise<void> {
    event.preventDefault()

    if (newPassword !== confirmPassword) {
      setError('Those passwords do not match.')
      return
    }

    setBusy(true)
    setError(null)

    try {
      await handleOutcome(await completeNewPassword(newPassword), 'password')
    } finally {
      setBusy(false)
    }
  }

  async function submitOtpRequest(event: SyntheticEvent): Promise<void> {
    event.preventDefault()

    if (otpEmail.trim() === '') {
      setError('Enter your email address.')
      return
    }

    setBusy(true)
    setError(null)

    try {
      await handleOutcome(await requestEmailOtp(otpEmail), 'otp')
    } finally {
      setBusy(false)
    }
  }

  async function submitOtpCode(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      const outcome = await confirmEmailOtp(otpCode)

      if (outcome.status === 'failed') {
        setOtpCode('')
      }

      await handleOutcome(outcome, 'otp')
    } finally {
      setBusy(false)
    }
  }

  async function submitTotpCode(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      const outcome = await confirmTotpCode(totpCode)

      if (outcome.status === 'failed') {
        setTotpCode('')
      }

      await handleOutcome(outcome, method)
    } finally {
      setBusy(false)
    }
  }

  function restartOtp(): void {
    setOtpStep('request')
    setOtpEmail('')
    setOtpCode('')
    setError(null)
  }

  if (session) {
    const from = (location.state as { from?: string } | null)?.from
    const returnable = from !== undefined && from !== '/login' && canOpenPath(session.role, from)

    return <Navigate to={returnable ? from : '/'} replace />
  }

  return (
    <main className="flex min-h-dvh items-center justify-center px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-2.5">
            <span className="canopy-logo-img-fallback">
              <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M8 1.5C5.5 1.5 3.5 3.8 3.5 6.5c0 1.6.7 3 1.8 3.9L4.8 13h6.4l-.5-2.6c1.1-.9 1.8-2.3 1.8-3.9 0-2.7-2-5-4.5-5z"
                  fill="white"
                  opacity=".95"
                />
                <path d="M6.2 13v1.2a1.3 1.3 0 002.6 0V13" fill="white" opacity=".7" />
              </svg>
            </span>
            <span className="canopy-logo-text text-xl font-bold">
              Serent <span>Canopy</span>
            </span>
          </div>
          <p className="canopy-page-sub mt-2">
            Portfolio AI readiness intelligence for Serent Capital.
          </p>
        </div>

        {onEntryScreen && (
          <div
            role="group"
            aria-label="Sign-in method"
            className="border-border mb-6 flex border-b"
          >
            <button
              type="button"
              aria-pressed={method === 'password'}
              className={`${tabClass} ${method === 'password' ? tabActiveClass : tabIdleClass}`}
              onClick={() => {
                switchMethod('password')
              }}
            >
              Email &amp; Password
            </button>
            <button
              type="button"
              aria-pressed={method === 'otp'}
              className={`${tabClass} ${method === 'otp' ? tabActiveClass : tabIdleClass}`}
              onClick={() => {
                switchMethod('otp')
              }}
            >
              Login via Email OTP
            </button>
          </div>
        )}

        <div className="bg-surface border-border rounded-xl border p-7 shadow-sm">
          <div className="mb-6">
            <h1 className="canopy-page-title">
              {needsTotp
                ? 'Verify it’s you'
                : needsNewPassword
                  ? 'Choose a new password'
                  : method === 'otp'
                    ? otpStep === 'verify'
                      ? 'Enter your code'
                      : 'Sign in with a code'
                    : 'Sign in'}
            </h1>
            <p className="canopy-page-sub">{intro}</p>
          </div>

          {notice !== null && (
            <div
              role="alert"
              aria-live="polite"
              className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-800"
            >
              {notice}
            </div>
          )}

          {needsTotp ? (
            <form
              onSubmit={(event) => {
                void submitTotpCode(event)
              }}
              noValidate
            >
              <div className="mb-6">
                <label className={labelClass} htmlFor="totp-code">
                  Authentication code
                </label>
                <input
                  id="totp-code"
                  className={`${fieldClass} font-mono tracking-[3px]`}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  autoFocus
                  value={totpCode}
                  onChange={(event) => {
                    setTotpCode(event.target.value)
                  }}
                  disabled={busy}
                />
              </div>
              <button type="submit" className={submitClass} disabled={busy}>
                {busy ? 'Verifying…' : 'Verify'}
              </button>
              <button type="button" className={secondaryClass} onClick={startOver}>
                Start over
              </button>
            </form>
          ) : needsNewPassword ? (
            <form
              onSubmit={(event) => {
                void submitNewPassword(event)
              }}
              noValidate
            >
              <div className="mb-4">
                <label className={labelClass} htmlFor="new-password">
                  New password
                </label>
                <input
                  id="new-password"
                  className={fieldClass}
                  type="password"
                  autoComplete="new-password"
                  required
                  value={newPassword}
                  onChange={(event) => {
                    setNewPassword(event.target.value)
                  }}
                  disabled={busy}
                />
              </div>
              <div className="mb-6">
                <label className={labelClass} htmlFor="confirm-password">
                  Confirm new password
                </label>
                <input
                  id="confirm-password"
                  className={fieldClass}
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value)
                  }}
                  disabled={busy}
                />
              </div>
              <button type="submit" className={submitClass} disabled={busy}>
                {busy ? 'Saving…' : 'Set password and sign in'}
              </button>
              <button type="button" className={secondaryClass} onClick={startOver}>
                Start over
              </button>
            </form>
          ) : method === 'otp' ? (
            otpStep === 'verify' ? (
              <form
                onSubmit={(event) => {
                  void submitOtpCode(event)
                }}
                noValidate
              >
                <div className="mb-6">
                  <label className={labelClass} htmlFor="otp-code">
                    Code
                  </label>
                  <input
                    id="otp-code"
                    className={`${fieldClass} font-mono tracking-[3px]`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={8}
                    required
                    value={otpCode}
                    onChange={(event) => {
                      setOtpCode(event.target.value)
                    }}
                    disabled={busy}
                  />
                </div>
                <button type="submit" className={submitClass} disabled={busy}>
                  {busy ? 'Verifying…' : 'Verify code'}
                </button>
                <button type="button" className={secondaryClass} onClick={restartOtp}>
                  Use a different email
                </button>
              </form>
            ) : (
              <form
                onSubmit={(event) => {
                  void submitOtpRequest(event)
                }}
                noValidate
              >
                <div className="mb-6">
                  <label className={labelClass} htmlFor="otp-email">
                    Email
                  </label>
                  <input
                    id="otp-email"
                    className={fieldClass}
                    placeholder="you@company.com"
                    type="email"
                    autoComplete="username"
                    required
                    value={otpEmail}
                    onChange={(event) => {
                      setOtpEmail(event.target.value)
                    }}
                    disabled={busy}
                  />
                </div>
                <button type="submit" className={submitClass} disabled={busy}>
                  {busy ? 'Sending code…' : 'Send code'}
                </button>
              </form>
            )
          ) : (
            <form
              onSubmit={(event) => {
                void submitCredentials(event)
              }}
              noValidate
            >
              <div className="mb-4">
                <label className={labelClass} htmlFor="email">
                  Email
                </label>
                <input
                  id="email"
                  className={fieldClass}
                  placeholder="you@company.com"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value)
                  }}
                  disabled={busy}
                />
              </div>
              <div className="mb-6">
                <label className={labelClass} htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  className={fieldClass}
                  placeholder="••••••••"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => {
                    setPassword(event.target.value)
                  }}
                  disabled={busy}
                />
              </div>
              <button type="submit" className={submitClass} disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
            </form>
          )}
        </div>

        {onEntryScreen && (
          <p className="text-text-3 mt-4 text-center text-[13px]">
            Access is by invitation. Contact your Serent administrator if you need an account.
          </p>
        )}
      </div>
    </main>
  )
}
